import type { DisciplineSlug } from "@jumpbean/shared";

export type SequenceItem = string | number;

export interface SequenceItemDisplay {
  value: SequenceItem;
  color?: string;
  emoji?: string;
  imageUrl?: string;
}

export interface SequenceChallengeContent {
  /** The pattern sequence with one missing item */
  pattern: SequenceItem[];
  /** Index of the missing item in the pattern */
  missingIndex: number;
  /** Available options to choose from */
  options: SequenceItem[];
  /** The correct answer */
  correctAnswer: SequenceItem;
  /** Optional display mapping for items */
  displayMap?: Record<string, SequenceItemDisplay>;
}

export interface SequenceChallengeProps {
  /** Challenge content configuration */
  content: SequenceChallengeContent;
  /** Discipline for theming */
  discipline?: DisciplineSlug;
  /** Called when an answer is selected */
  onAnswer: (answer: SequenceItem) => void;
  /** Currently selected answer */
  selectedAnswer: SequenceItem | null;
  /** Whether answer has been submitted */
  isSubmitted?: boolean;
  /** Whether the submitted answer was correct */
  isCorrect?: boolean;
}

/**
 * Sequence Challenge Component
 * Displays a pattern with one missing item.
 * Children select the correct item to complete the pattern.
 */
export function SequenceChallenge({
  content,
  discipline = "patterns",
  onAnswer,
  selectedAnswer,
  isSubmitted = false,
  isCorrect = false,
}: SequenceChallengeProps) {
  // Get display info for an item
  const getItemDisplay = (value: SequenceItem): SequenceItemDisplay => {
    const mapped = content.displayMap?.[String(value)];
    if (mapped) {
      return mapped;
    }
    // Default color mapping for pattern colors
    return {
      value,
      color: getDefaultColor(value),
      emoji: typeof value === "string" ? undefined : undefined,
    };
  };

  return (
    <div className="flex flex-col items-center gap-8">
      {/* Pattern Display */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-neutral-200 w-full">
        <h3 className="text-center font-display font-bold text-body-lg text-neutral-700 mb-6">
          Complete the pattern!
        </h3>
        <div className="flex flex-wrap justify-center items-center gap-3">
          {content.pattern.map((item, index) => {
            const isMissing = index === content.missingIndex;
            const display = isMissing && selectedAnswer !== null
              ? getItemDisplay(selectedAnswer)
              : getItemDisplay(item);

            return (
              <div key={index} className="flex items-center gap-2">
                {isMissing ? (
                  <div
                    className={`
                      w-16 h-16 md:w-20 md:h-20
                      rounded-xl border-4 border-dashed
                      flex items-center justify-center
                      transition-all duration-300
                      ${
                        selectedAnswer !== null
                          ? `${getItemBgColor(display)} border-solid border-neutral-400`
                          : "bg-neutral-100 border-neutral-400"
                      }
                      ${isSubmitted && isCorrect ? "animate-celebrate" : ""}
                    `}
                  >
                    {selectedAnswer !== null ? (
                      <ItemContent display={display} />
                    ) : (
                      <span className="text-3xl text-neutral-400">?</span>
                    )}
                  </div>
                ) : (
                  <div
                    className={`
                      w-16 h-16 md:w-20 md:h-20
                      rounded-xl border-4 border-neutral-300
                      flex items-center justify-center
                      ${getItemBgColor(display)}
                    `}
                  >
                    <ItemContent display={display} />
                  </div>
                )}

                {/* Arrow between items */}
                {index < content.pattern.length - 1 && (
                  <span className="text-neutral-400 text-xl hidden sm:block">
                    →
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Answer Options */}
      <div className="w-full">
        <h3 className="text-center font-display font-bold text-body-lg text-neutral-600 mb-4">
          What comes next?
        </h3>
        <div className="flex flex-wrap justify-center gap-4">
          {content.options.map((option, index) => {
            const display = getItemDisplay(option);
            const isSelected = selectedAnswer === option;
            const isCorrectOption = option === content.correctAnswer;

            return (
              <button
                key={index}
                type="button"
                onClick={() => !isSubmitted && onAnswer(option)}
                disabled={isSubmitted}
                className={`
                  w-20 h-20 md:w-24 md:h-24
                  rounded-xl border-4
                  flex items-center justify-center
                  transition-all duration-200
                  ${getOptionStyles(isSelected, isSubmitted, isCorrect, isCorrectOption, discipline, display)}
                `}
                aria-label={`Select ${option}`}
                aria-pressed={isSelected}
              >
                <ItemContent display={display} />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * Render item content (color, emoji, image, or text)
 */
function ItemContent({ display }: { display: SequenceItemDisplay }) {
  if (display.imageUrl) {
    return (
      <img
        src={display.imageUrl}
        alt={String(display.value)}
        className="w-12 h-12 object-contain"
      />
    );
  }

  if (display.emoji) {
    return <span className="text-3xl md:text-4xl">{display.emoji}</span>;
  }

  // For color-only items, show a colored circle
  if (display.color && typeof display.value === "string" && isColorName(display.value)) {
    return null; // Background color handles it
  }

  return (
    <span className="font-display font-bold text-body-xl text-neutral-800">
      {display.value}
    </span>
  );
}

/**
 * Check if a value is a color name
 */
function isColorName(value: string): boolean {
  const colors = ["red", "blue", "green", "yellow", "purple", "orange", "pink", "cyan"];
  return colors.includes(value.toLowerCase());
}

/**
 * Get default color for pattern items
 */
function getDefaultColor(value: SequenceItem): string | undefined {
  if (typeof value !== "string") return undefined;

  const colorMap: Record<string, string> = {
    red: "#FF6B6B",
    blue: "#74B9FF",
    green: "#98D89B",
    yellow: "#FFE66D",
    purple: "#A29BFE",
    orange: "#FFB347",
    pink: "#FF9EBC",
    cyan: "#00D4FF",
  };

  return colorMap[value.toLowerCase()];
}

/**
 * Get background color class for item
 */
function getItemBgColor(display: SequenceItemDisplay): string {
  if (display.color) {
    // Use inline style for custom colors
    return "";
  }
  return "bg-white";
}

/**
 * Get styles for answer option based on state
 */
function getOptionStyles(
  isSelected: boolean,
  isSubmitted: boolean,
  isCorrect: boolean,
  isCorrectOption: boolean,
  discipline: DisciplineSlug,
  display: SequenceItemDisplay
): string {
  // Get background color from display
  const bgStyle = display.color
    ? `bg-[${display.color}]`
    : "bg-white";

  if (isSubmitted) {
    if (isCorrectOption) {
      return `
        ${bgStyle}
        border-meadow-500
        shadow-[0_4px_0_0_theme(colors.meadow.700)]
        animate-celebrate
      `;
    }
    if (isSelected && !isCorrect) {
      return `
        ${bgStyle}
        border-neutral-400
        opacity-75
      `;
    }
    return `
      ${bgStyle}
      border-neutral-300
      opacity-50
    `;
  }

  if (isSelected) {
    return `
      ${bgStyle}
      ${getDisciplineSelectedBorder(discipline)}
      shadow-[0_4px_0_0_currentColor]
      -translate-y-1
      ring-4 ring-sunshine-300
    `;
  }

  return `
    ${bgStyle}
    border-neutral-300
    hover:border-neutral-400
    active:scale-95
    shadow-[0_3px_0_0_theme(colors.neutral.300)]
    hover:shadow-[0_1px_0_0_theme(colors.neutral.300)]
    hover:-translate-y-0.5
  `;
}

/**
 * Get border style for selected state based on discipline
 */
function getDisciplineSelectedBorder(discipline: DisciplineSlug): string {
  const styles: Record<DisciplineSlug, string> = {
    math: "border-luna-dark",
    spelling: "border-petal-dark",
    patterns: "border-prism-red",
    typing: "border-whiskers-dark",
    programming: "border-pixel-dark",
  };
  return styles[discipline];
}

export default SequenceChallenge;
